/**
 * The shader everything built wears — stations, ships, manmade anomalies.
 *
 * Natural things in this game are read by their colour and their shading: a
 * planet is a lit sphere, a star is a ball of light. Built things cannot be read
 * that way. A station is a grey slab covered in grey boxes, and past a few
 * hundred metres the shading flattens into a single grey blob with no scale to
 * it — you cannot tell a two-pad outpost from an eighteen-pad yard, and you
 * cannot tell whether it is close and small or far and huge.
 *
 * Drawing the edges fixes both, and it is the whole reason the decks are packed
 * with surface detail in the first place: the detail is what the edges are
 * *of*. A hundred lit boxes is a texture; a hundred outlined boxes is a
 * structure with a readable size.
 *
 * ── Why the edges are in the shader ─────────────────────────────────────────
 *
 * The obvious implementation is `EdgesGeometry` plus `LineSegments`, and it is
 * wrong here for two reasons. A large station carries the better part of a
 * thousand props, so that is a thousand extra objects to build, upload and
 * draw. And GL line width is capped at one pixel on virtually every desktop
 * driver, so the lines fade out at exactly the distance they are needed.
 *
 * Doing it in the fragment shader costs no extra geometry, survives instancing,
 * and puts the line width under our control — which is what lets the edges get
 * *stronger* as a structure shrinks on screen instead of disappearing.
 */

import * as THREE from 'three';

/** Far enough out of range that the shader never finds this side near enough. */
const SUPPRESSED = 10;

/** Crease angle past which a shared edge counts as a real one, in degrees. */
const CREASE_DEGREES = 20;

/**
 * Tag a geometry's triangles with which of their edges are real.
 *
 * The fragment shader finds an edge by asking how close the fragment is to the
 * triangle's boundary, which it can only do if each vertex carries its position
 * within the triangle — so the geometry has to be de-indexed first, since
 * shared vertices cannot hold three different values.
 *
 * The catch is that triangles are not faces. A box quad is two triangles whose
 * shared diagonal is a triangle boundary but not an edge of anything, and an
 * extruded deck's top is a fan of triangles all lying in one plane. Left alone
 * every flat surface gets drawn over with its own triangulation.
 *
 * So an edge is only real if it is a silhouette (belonging to one triangle) or
 * a crease (its two triangles face meaningfully different directions) — the
 * same test `EdgesGeometry` makes, applied as a mask instead of as new
 * geometry. Everything else has its coordinate pushed out of range: the shader
 * still measures the distance to that side, and never finds it close enough.
 */
export function withEdges(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  const flat = geometry.index ? geometry.toNonIndexed() : geometry;
  const position = flat.getAttribute('position');
  const count = position.count;

  const vertex = (i: number) => new THREE.Vector3().fromBufferAttribute(position, i);

  // Vertices that coincide are welded by position, since a de-indexed geometry
  // has no other way to know two triangles meet.
  const nameOf = (v: THREE.Vector3): string =>
    `${Math.round(v.x * 1e4)},${Math.round(v.y * 1e4)},${Math.round(v.z * 1e4)}`;
  const edgeName = (a: THREE.Vector3, b: THREE.Vector3): string => {
    const [p, q] = [nameOf(a), nameOf(b)];
    return p < q ? `${p}|${q}` : `${q}|${p}`;
  };

  // Barycentric component k measures the distance to the side *opposite*
  // vertex k, so component 0 is the side (v1, v2), and so on round.
  const sides: [number, number][] = [
    [1, 2],
    [2, 0],
    [0, 1],
  ];

  const normals = new Map<string, THREE.Vector3[]>();

  /** `null` for a degenerate triangle, whose normal would be meaningless. */
  const faceNormal = (i: number): THREE.Vector3 | null => {
    const normal = new THREE.Vector3()
      .subVectors(vertex(i + 1), vertex(i))
      .cross(new THREE.Vector3().subVectors(vertex(i + 2), vertex(i)));

    // A zero-area triangle normalises to NaN, and NaN loses every comparison
    // silently — so a sliver in the mesh would quietly suppress real edges
    // around it rather than being ignored.
    return normal.lengthSq() < 1e-20 ? null : normal.normalize();
  };

  for (let i = 0; i < count; i += 3) {
    const normal = faceNormal(i);
    if (!normal) continue;
    for (const [a, b] of sides) {
      const name = edgeName(vertex(i + a), vertex(i + b));
      const seen = normals.get(name);
      if (seen) seen.push(normal);
      else normals.set(name, [normal]);
    }
  }

  const creaseLimit = Math.cos((CREASE_DEGREES * Math.PI) / 180);
  const edge = new Float32Array(count * 3);

  for (let i = 0; i < count; i += 3) {
    for (let corner = 0; corner < 3; corner++) {
      for (let axis = 0; axis < 3; axis++) {
        edge[(i + corner) * 3 + axis] = corner === axis ? 1 : 0;
      }
    }

    for (let axis = 0; axis < 3; axis++) {
      const [a, b] = sides[axis]!;
      const faces = normals.get(edgeName(vertex(i + a), vertex(i + b))) ?? [];

      // One face means a silhouette edge, which always draws. Two facing nearly
      // the same way is a triangulation seam across a flat surface.
      //
      // Compared on the absolute dot product, so a pair of triangles wound
      // opposite ways still reads as coplanar. Winding is not guaranteed
      // consistent across a triangulated import, and treating a winding flip as
      // a 180° crease drew a line along every seam of every flat face.
      const real =
        faces.length === 1 ||
        faces.some((n) => Math.abs(n.dot(faces[0]!)) < creaseLimit);
      if (real) continue;

      for (let corner = 0; corner < 3; corner++) {
        edge[(i + corner) * 3 + axis] = SUPPRESSED;
      }
    }
  }

  flat.setAttribute('aEdge', new THREE.BufferAttribute(edge, 3));
  return flat;
}

const vertexShader = /* glsl */ `
  // Must come first. The log-depth chunks call helpers that live in <common>,
  // and without it the shader still compiles — it just quietly renders nothing.
  #include <common>
  #include <logdepthbuf_pars_vertex>

  attribute vec3 aEdge;

  varying vec3 vEdge;
  varying vec3 vNormal;
  varying vec3 vWorldPosition;
  varying vec3 vLocalPosition;
  varying float vViewDepth;
  varying float vTint;

  void main() {
    vEdge = aEdge;
    vLocalPosition = position;
    // Instanced meshes carry their transform per instance, and three.js only
    // folds it into the normal matrix for its own materials — so it is applied
    // here explicitly, or every instance is lit as if it were unrotated.
    #ifdef USE_INSTANCING
      vNormal = normalize(mat3(modelMatrix) * mat3(instanceMatrix) * normal);
      vec4 world = modelMatrix * instanceMatrix * vec4(position, 1.0);
    #else
      vNormal = normalize(mat3(modelMatrix) * normal);
      vec4 world = modelMatrix * vec4(position, 1.0);
    #endif

    /*
     * A number that is the same everywhere on this instance, and different on
     * the next one.
     *
     * Taken from the instance's own origin rather than from the fragment's world
     * position: the latter varies across a rock's surface, which would speckle
     * each one instead of colouring it. The origin is constant per instance and
     * already unique — the rocks are scattered — so it is a seed nobody has to
     * generate, store or send.
     */
    #ifdef USE_INSTANCING
      vec3 tintSeed = instanceMatrix[3].xyz;
    #else
      vec3 tintSeed = modelMatrix[3].xyz;
    #endif

    /*
     * A hash that survives being a long way from the origin.
     *
     * The usual fract(sin(dot(p, k)) * big) falls apart out here. Rocks sit
     * thousands of units out, so the dot product lands around 1e5, and sin of a
     * number that size in a 32-bit float has almost no precision left — every
     * rock in a belt hashed to nearly the same value, which is why the variation
     * was invisible however far the spread was turned up.
     *
     * Folding the position into the unit range *first* keeps the input small
     * while preserving the differences that matter: neighbouring rocks are a
     * unit or two apart, which is a tenth of a turn through this and lands them
     * in quite different places.
     */
    vec3 h = fract(tintSeed * 0.1031);
    h += dot(h, h.yzx + 33.33);
    vTint = fract((h.x + h.y) * h.z);

    vWorldPosition = world.xyz;
    vec4 view = viewMatrix * world;
    vViewDepth = -view.z;
    gl_Position = projectionMatrix * view;

    #include <logdepthbuf_vertex>
  }
`;

const fragmentShader = /* glsl */ `
  #include <common>
  #include <logdepthbuf_pars_fragment>

  precision highp float;

  uniform vec3 uFaceColor;
  uniform vec3 uEdgeColor;
  uniform vec3 uLightPosition;
  uniform float uEdgeWidth;
  uniform float uEdgeWorldWidth;
  uniform float uTintSpread;
  uniform float uFadeDistance;
  uniform float uAmbient;
  uniform float uCutawayTop;
  uniform float uCutawayBottom;

  varying vec3 vEdge;
  varying vec3 vNormal;
  varying vec3 vWorldPosition;
  varying vec3 vLocalPosition;
  varying float vViewDepth;
  /** Per-instance, from the instance's own origin. Zero without instancing. */
  varying float vTint;

  /*
   * Hue rotation, the cheap way.
   *
   * Full RGB to HSV and back is a pile of branches to shift one number. This
   * spins the colour about the grey axis instead, which is the same operation
   * expressed as a rotation — and at the small angles used here the difference
   * from a true hue shift is not something anyone can see.
   */
  vec3 shiftHue(vec3 color, float turns) {
    const vec3 axis = vec3(0.57735);
    float angle = turns * 6.2831853;
    float c = cos(angle);

    return color * c + cross(axis, color) * sin(angle) + axis * dot(axis, color) * (1.0 - c);
  }

  void main() {
    #include <logdepthbuf_fragment>

    // Distance to the nearest triangle side, measured in pixels rather than in
    // model units. That is the whole trick: dividing by the screen-space rate
    // of change makes the line the same width whether the box is filling the
    // view or is four pixels across.
    vec3 delta = fwidth(vEdge);
    vec3 pixels = vEdge / max(delta, vec3(1e-6));
    float nearest = min(min(pixels.x, pixels.y), pixels.z);

    /*
     * How wide the line should be, in pixels.
     *
     * A fixed pixel width is right for the things this shader was built for: a
     * station deck should read the same whether you are docked on it or looking
     * at it from across the system, and its outline is the *drawing*, not a
     * property of the object.
     *
     * It is wrong for anything small and numerous. A rock a few units across,
     * seen from far enough away to be a dozen pixels, gets the same line as a
     * kilometre-wide deck — so distant asteroids come out looking like ink
     * blots while near ones look fine.
     *
     * uEdgeWorldWidth switches to a width measured in model units. length(delta)
     * is roughly how much model space one pixel covers, so dividing by it turns
     * a model width into a pixel width — which shrinks with distance, the way
     * an edge on a real object does. The antialiasing band stays in pixels
     * either way, which is the only place it makes sense.
     *
     * Floored at just under a pixel: past that a line stops being thin and
     * starts flickering, and a rock that dissolves into nothing at range reads
     * as a bug rather than as distance.
     */
    float width = uEdgeWorldWidth > 0.0
      ? max(0.7, uEdgeWorldWidth / max(length(delta), 1e-6))
      : uEdgeWidth;

    float edge = 1.0 - smoothstep(width - 0.75, width + 0.75, nearest);

    // The light falls on the *lines*, not on the faces.
    //
    // Faces are unlit black: they exist to occlude, so that edges on the far
    // side of a hull are hidden and the thing reads as solid rather than as a
    // wireframe you can see through. All the shading budget then goes into the
    // lines, where it still describes the form — an edge between two faces
    // turned toward the light is bright, one turned away is nearly gone — while
    // the silhouette stays a clean stroke against space.
    // Lit from the star itself, so a station's bright side swings round as it
    // orbits — the same light every planet in the system is answering to.
    vec3 normal = normalize(vNormal);
    vec3 toLight = normalize(uLightPosition - vWorldPosition);
    float lambert = max(dot(normal, toLight), 0.0);
    /*
     * The rock's own colour, from where it happens to be.
     *
     * A belt used to need one material per shade — a tint is a uniform, so six
     * colours meant six draw calls per field and six was the limit anyone would
     * accept. Deriving it from the instance's position gives every rock its own
     * shade for nothing, and puts the whole field back into a single draw.
     *
     * Rotated about the lead colour rather than picked freely: a field still
     * reads as one place, which is the thing the lead colour was for.
     */
    vec3 edgeColor = uTintSpread > 0.0
      ? shiftHue(uEdgeColor, (vTint - 0.5) * uTintSpread)
      : uEdgeColor;

    vec3 line = edgeColor * (uAmbient + (1.0 - uAmbient) * lambert);

    // Edges strengthen as a structure shrinks on screen. Close up the lines
    // describe the form and can afford to be delicate; far off they are the
    // only thing left saying how big this is and how far away.
    float far = clamp(vViewDepth / uFadeDistance, 0.0, 1.0);
    float strength = mix(0.8, 1.0, far);

    vec3 colour = mix(uFaceColor, line, edge * strength);

    // Cutaway: the walls dissolve toward the top so you can see in, and the
    // floor stays solid so the ship still reads as a thing standing on a pad
    // rather than a floating outline. A downward-facing surface is the floor,
    // whatever height it happens to be at.
    float alpha = 1.0;
    if (uCutawayTop > uCutawayBottom) {
      float up = clamp(
        (vLocalPosition.z - uCutawayBottom) / (uCutawayTop - uCutawayBottom),
        0.0,
        1.0
      );
      float facingDown = clamp(-normal.z, 0.0, 1.0);
      alpha = max(mix(1.0, 0.06, up), facingDown);
      // Edges keep their weight regardless, or the shape loses its outline
      // exactly where it is most transparent.
      alpha = max(alpha, edge * 0.85);
    }

    gl_FragColor = vec4(colour, alpha);
  }
`;

/**
 * Black, with the faintest blue cast to avoid banding against the background.
 *
 * This is effectively invisible once converted to linear space, and that is the
 * intent: faces are not there to be seen. They are there to *occlude*, so that
 * edges on the far side of a hull are hidden and the object reads as a solid
 * body rather than as a wireframe you can see straight through. Depth is
 * written whatever the colour, so hidden-line removal costs nothing.
 */
const FACE_COLOR = 0x04060b;

export interface StructureMaterialOptions {
  /** The structure's colour — carried by its lines, since the faces are unlit. */
  color: THREE.ColorRepresentation;
  /** Face fill. Near-black by default; it is there to occlude, not to be seen. */
  faceColor?: THREE.ColorRepresentation;
  /** Where the light is, in world space. The system's star sits at the origin. */
  lightPosition?: THREE.Vector3;
  /** Line half-width in pixels. */
  edgeWidth?: number;
  /**
   * Line width in model units, instead of pixels.
   *
   * Leave unset for anything built — a station's outline should not thin out as
   * you back away from it. Set it for small, numerous things whose outline is
   * part of the object rather than a label on it: see the note in the fragment
   * shader for why distant asteroids looked inked in.
   */
  edgeWorldWidth?: number;
  /**
   * How far each instance's line may drift from the given colour, in turns of
   * the hue wheel.
   *
   * Zero — the default — means every instance is the colour it was given, which
   * is what anything built wants. A small number gives a field of instances
   * individual shades without a material each; see the fragment shader.
   */
  tintSpread?: number;
  /** View distance by which edges reach full strength. */
  fadeDistance?: number;
  /** How lit an edge facing away from the light still is, 0..1. */
  ambient?: number;
  /**
   * Fade the surface out toward the top, in object-space z.
   *
   * For looking inside a hull: walls go clear as they rise, the floor stays
   * solid, and edges keep their weight so the silhouette survives. Omit for the
   * normal opaque surface.
   */
  cutaway?: { bottom: number; top: number };
}

export function createStructureMaterial(
  options: StructureMaterialOptions,
): THREE.ShaderMaterial {
  // The colour now belongs to the lines. Lifted toward white, because a line
  // drawn in the raw surface colour is too dark to carry the whole structure
  // once the faces stop contributing anything.
  const edgeColor = new THREE.Color(options.color)
    .clone()
    .lerp(new THREE.Color(0xffffff), 0.35);

  return new THREE.ShaderMaterial({
    vertexShader,
    fragmentShader,
    uniforms: {
      uFaceColor: { value: new THREE.Color(options.faceColor ?? FACE_COLOR) },
      uEdgeColor: { value: edgeColor },
      uLightPosition: { value: (options.lightPosition ?? new THREE.Vector3(0, 0, 0)).clone() },
      uEdgeWidth: { value: options.edgeWidth ?? 1.1 },
      uEdgeWorldWidth: { value: options.edgeWorldWidth ?? 0 },
      uTintSpread: { value: options.tintSpread ?? 0 },
      uFadeDistance: { value: options.fadeDistance ?? 3000 },
      // Never fully dark: an unlit edge still has to describe the silhouette,
      // or the far side of every hull disappears and the shape breaks up.
      uAmbient: { value: options.ambient ?? 0.32 },
      uCutawayBottom: { value: options.cutaway?.bottom ?? 0 },
      uCutawayTop: { value: options.cutaway?.top ?? 0 },
    },
    transparent: Boolean(options.cutaway),
    // Without this the far wall is discarded before the near one is drawn, and
    // the inside of the hull — the thing a cutaway exists to show — disappears.
    depthWrite: !options.cutaway,
    side: options.cutaway ? THREE.DoubleSide : THREE.FrontSide,
  });
}
